"use client";

import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PARTNER_CATEGORIES, PARTNER_CATEGORY_LABELS, PartnerCategory } from "@/lib/partners";
import { AlertCircle, Handshake, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";

interface PublicPartner {
	id: string;
	name: string;
	category: PartnerCategory;
	website_url: string;
	logo_url: string;
	display_order: number;
}

interface PartnerCategoryGroup {
	key: PartnerCategory;
	label: string;
	partners: PublicPartner[];
}

const defaultGroups: PartnerCategoryGroup[] = PARTNER_CATEGORIES.map((category) => ({
	key: category,
	label: PARTNER_CATEGORY_LABELS[category],
	partners: [],
}));

const categoryBadgeStyles: Record<PartnerCategory, string> = {
	hebergement: "bg-emerald-100 text-emerald-700 border-emerald-200",
	restauration: "bg-orange-100 text-orange-700 border-orange-200",
	location_vehicule: "bg-teal-100 text-teal-700 border-teal-200",
	divers: "bg-sky-100 text-sky-700 border-sky-200",
};

const categoryBarStyles: Record<PartnerCategory, string> = {
	hebergement: "from-emerald-500 to-teal-500",
	restauration: "from-orange-500 to-yellow-500",
	location_vehicule: "from-teal-500 to-cyan-500",
	divers: "from-sky-500 to-emerald-500",
};

export default function PartnersPage() {
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [categories, setCategories] = useState<PartnerCategoryGroup[]>(defaultGroups);

	useEffect(() => {
		const fetchPartners = async () => {
			try {
				setLoading(true);
				setError(null);
				const response = await fetch("/api/partners", { cache: "no-store" });
				if (!response.ok) {
					throw new Error("Impossible de charger les partenaires");
				}

				const data = await response.json();
				if (Array.isArray(data?.categories)) {
					const ordered = PARTNER_CATEGORIES.map((category) => {
						const existing = data.categories.find((group: PartnerCategoryGroup) => group.key === category);
						return {
							key: category,
							label: PARTNER_CATEGORY_LABELS[category],
							partners: Array.isArray(existing?.partners) ? existing.partners : [],
						};
					});
					setCategories(ordered);
				} else {
					setCategories(defaultGroups);
				}
			} catch (err) {
				console.error("Error fetching partners:", err);
				setError(err instanceof Error ? err.message : "Une erreur est survenue");
			} finally {
				setLoading(false);
			}
		};

		fetchPartners();
	}, []);

	const totalPartners = useMemo(
		() => categories.reduce((total, category) => total + category.partners.length, 0),
		[categories],
	);

	return (
		<div className="min-h-screen bg-gradient-to-b from-sky-50 to-emerald-50">
			{/* Header */}
			<header className="bg-white/90 backdrop-blur-md shadow-sm sticky top-0 z-50">
				<div className="container mx-auto px-4 py-4">
					<div className="flex items-center justify-between">
						<Link href="/" className="flex items-center space-x-3">
							<Image
								src="/images/logo-hd.png"
								alt="Soleil & Découverte"
								width={60}
								height={60}
								className="object-contain"
							/>
							<div>
								<h1 className="text-2xl font-bold bg-gradient-to-r from-orange-500 to-yellow-500 bg-clip-text text-transparent">
									Soleil & Découverte
								</h1>
								<p className="text-sm text-emerald-600">L'aventure au cœur de la Guadeloupe</p>
							</div>
						</Link>

						<nav className="hidden md:flex items-center space-x-8">
							<Link
								href="/"
								className="text-gray-700 hover:text-emerald-600 transition-colors font-medium"
							>
								Accueil
							</Link>
							<Link
								href="/activites"
								className="text-gray-700 hover:text-emerald-600 transition-colors font-medium"
							>
								Activités
							</Link>
							<Link href="/partenaires" className="text-emerald-600 font-semibold">
								Partenaires
							</Link>
							<Link
								href="/contact"
								className="text-gray-700 hover:text-emerald-600 transition-colors font-medium"
							>
								Contact
							</Link>
							<Link href="/reservation">
								<Button className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-6 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300">
									Réserver
								</Button>
							</Link>
						</nav>

						{/* Mobile menu button */}
						<Button
							variant="ghost"
							className="md:hidden"
							onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
						>
							<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M4 6h16M4 12h16M4 18h16"
								/>
							</svg>
						</Button>
					</div>
				</div>

				{/* Mobile Menu */}
				{isMobileMenuOpen && (
					<div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
						<div className="container mx-auto px-4 py-4">
							<nav className="flex flex-col space-y-4">
								<Link
									href="/"
									className="text-gray-700 hover:text-emerald-600 transition-colors font-medium py-2"
									onClick={() => setIsMobileMenuOpen(false)}
								>
									Accueil
								</Link>
								<Link
									href="/activites"
									className="text-gray-700 hover:text-emerald-600 transition-colors font-medium py-2"
									onClick={() => setIsMobileMenuOpen(false)}
								>
									Activités
								</Link>
								<Link
									href="/partenaires"
									className="text-emerald-600 font-semibold py-2"
									onClick={() => setIsMobileMenuOpen(false)}
								>
									Partenaires
								</Link>
								<Link
									href="/contact"
									className="text-gray-700 hover:text-emerald-600 transition-colors font-medium py-2"
									onClick={() => setIsMobileMenuOpen(false)}
								>
									Contact
								</Link>
								<Link href="/reservation" onClick={() => setIsMobileMenuOpen(false)}>
									<Button className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-6 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 w-full">
										Réserver
									</Button>
								</Link>
							</nav>
						</div>
					</div>
				)}
			</header>

			{/* Hero Section */}
			<section className="relative py-20 overflow-hidden">
				<Image
					src="/images/partenaires-hero.png"
					alt="Nos partenaires"
					fill
					className="object-cover"
					style={{ zIndex: 1 }}
					priority
				/>
				<div className="absolute inset-0 bg-black/30" style={{ zIndex: 2 }}></div>

				<div className="container mx-auto px-4 relative z-10">
					<motion.div
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
						className="text-center max-w-4xl mx-auto"
					>
						<Badge className="mb-6 bg-white/20 text-white border-white/30 backdrop-blur-sm px-4 py-2 text-lg">
							🤝 Nos partenaires
						</Badge>
						<h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
							Un réseau solide pour
							<span className="block bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
								votre meilleure aventure
							</span>
						</h1>
						<p className="text-xl text-white/90 max-w-3xl mx-auto">
							Hébergement, restauration, location de véhicule et services utiles: nos partenaires
							contribuent à une expérience complète et fluide en Guadeloupe.
						</p>
					</motion.div>
				</div>
			</section>

			<section className="py-8 -mt-12 relative z-10">
				<div className="container mx-auto px-4">
					<div className="w-fit mx-auto rounded-3xl border border-emerald-100 bg-white/95 backdrop-blur-sm shadow-2xl px-6 py-5 md:px-8 md:py-6">
						<div className="flex justify-center">
							<div className="flex items-center gap-3">
								<div className="w-11 h-11 rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white flex items-center justify-center shadow-lg">
									<Handshake className="w-5 h-5" />
								</div>
								<div>
									<p className="text-sm uppercase tracking-wide text-emerald-700 font-semibold">
										Réseau partenaires
									</p>
									<p className="text-lg md:text-xl font-bold text-gray-900">
										{totalPartners} partenaire{totalPartners > 1 ? "s" : ""} actif
										{totalPartners > 1 ? "s" : ""}
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			<section className="pb-20">
				<div className="container mx-auto px-4">
					{loading && (
						<div className="max-w-3xl mx-auto rounded-3xl border border-emerald-100 bg-white/90 shadow-lg p-10 text-center">
							<div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 mb-4">
								<Loader2 className="w-7 h-7 animate-spin text-emerald-600" />
							</div>
							<p className="text-lg font-semibold text-emerald-700">Chargement des partenaires...</p>
						</div>
					)}

					{error && !loading && (
						<div className="max-w-3xl mx-auto rounded-3xl border border-red-200 bg-red-50/90 shadow-lg p-8 text-center">
							<div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-3">
								<AlertCircle className="w-6 h-6 text-red-600" />
							</div>
							<p className="text-lg font-semibold text-red-700 mb-2">
								Impossible de charger les partenaires
							</p>
							<p className="text-red-600">{error}</p>
						</div>
					)}

					{!loading && !error && (
						<div className="space-y-12">
							{categories.map((categoryGroup, index) => (
								<motion.section
									key={categoryGroup.key}
									initial={{ opacity: 0, y: 18 }}
									whileInView={{ opacity: 1, y: 0 }}
									transition={{ duration: 0.45, delay: index * 0.05 }}
									viewport={{ once: true }}
									className="rounded-3xl border border-emerald-100/70 bg-white/80 backdrop-blur-sm shadow-lg p-6 md:p-8"
								>
									<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
										<div>
											<h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
												{categoryGroup.label}
											</h2>
											<div
												className={`h-1.5 w-28 rounded-full bg-gradient-to-r ${categoryBarStyles[categoryGroup.key]}`}
											></div>
										</div>
										<span
											className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-semibold ${categoryBadgeStyles[categoryGroup.key]}`}
										>
											{categoryGroup.partners.length} partenaire
											{categoryGroup.partners.length > 1 ? "s" : ""}
										</span>
									</div>

									{categoryGroup.partners.length === 0 ? (
										<div className="rounded-2xl border border-dashed border-emerald-200 bg-emerald-50/60 p-8 text-center">
											<p className="text-gray-600">
												Aucun partenaire disponible pour cette catégorie pour le moment.
											</p>
										</div>
									) : (
										<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
											{categoryGroup.partners.map((partner) => (
												<a
													key={partner.id}
													href={partner.website_url}
													target="_blank"
													rel="noopener noreferrer"
													className="group relative overflow-hidden rounded-2xl border border-emerald-100 bg-white p-5 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
												>
													<div
														className={`absolute left-0 top-0 h-1 w-full bg-gradient-to-r ${categoryBarStyles[categoryGroup.key]}`}
													></div>

													<div className="w-24 h-24 mx-auto mt-2 mb-4 rounded-full bg-gradient-to-b from-sky-50 to-emerald-50 border border-emerald-100 flex items-center justify-center p-4 shadow-sm">
														<img
															src={partner.logo_url}
															alt={partner.name}
															className="max-w-full max-h-full object-contain rounded-full"
															onError={(event) => {
																event.currentTarget.src = "/images/logo-hd.png";
															}}
														/>
													</div>

													<div className="text-center space-y-2">
														<h3 className="text-base font-bold text-gray-900 leading-tight min-h-[2.5rem] flex items-center justify-center">
															{partner.name}
														</h3>
													</div>
												</a>
											))}
										</div>
									)}
								</motion.section>
							))}
						</div>
					)}
				</div>
			</section>

			<Footer />
		</div>
	);
}
